import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import type { TicketRow, TicketSlaClock } from '../easeedesk/columns'
import { apiGet, apiPost, apiPostForm } from './client'
import type { Named } from './shell'

/**
 * Contractul listelor de tichete: `/api/v2/easeedesk/tickets-table` si
 * `/closed-tickets-table` (DataTables, ca POST easeedesk/ticketsData si
 * closedTicketsData in portal), plus filtrele.
 *
 * Numele celor doua liste sunt CHIAR segmentele din portal - `tickets` si
 * `closed_tickets` - fiindca ele ajung in adresa: `/app/easeedesk/$list`.
 * La toate celelalte module adresa din /app e aceeasi cu cea din portal, si
 * de-aia redirectarea (`InventoreeCut` si fratii lui) mapeaza 1:1. Aici
 * scriau `active` si `closed`, nume inventate: `easeedesk/tickets` ar fi
 * ajuns in /app pe o adresa care nu exista, iar lista de tichete e chiar
 * intrarea in modul.
 */

export const TICKET_LISTS = ['tickets', 'closed_tickets'] as const
export type TicketListName = (typeof TICKET_LISTS)[number]

export function isTicketList(value: string): value is TicketListName {
  return (TICKET_LISTS as readonly string[]).includes(value)
}

/** Filtrele barei laterale, exact numele pe care le trimitea `TicketFilters`. */
export interface TicketFilters {
  company: string[]
  type: string[]
  status: string[]
  priority: string[]
  category: string[]
  subcategory: string[]
  assignee: string[]
  group: string[]
  escalation_type: string[]
  /** Aplicatia de business a aparatelor tichetului (`it_business_apps.ID`). */
  application: string[]
  advanced: string
  filterFrom: string
  filterTo: string
  taken_by_adam: boolean
}

export const EMPTY_TICKET_FILTERS: TicketFilters = {
  company: [],
  type: [],
  status: [],
  priority: [],
  category: [],
  subcategory: [],
  assignee: [],
  group: [],
  escalation_type: [],
  application: [],
  advanced: '',
  filterFrom: '',
  filterTo: '',
  taken_by_adam: false,
}

export const ID_FILTER_KEYS = [
  'company',
  'type',
  'status',
  'priority',
  'category',
  'subcategory',
  'assignee',
  'group',
  'escalation_type',
  'application',
] as const

export type IdFilterKey = (typeof ID_FILTER_KEYS)[number]

export interface TicketTableRequest {
  list: TicketListName
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
  filters: TicketFilters
}

export interface TicketTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: TicketRow[]
}

export interface TicketFilterOptions {
  types: Named[]
  statuses: Named[]
  priorities: Named[]
  categories: Named[]
  subcategories: Named[]
  groups: Named[]
  assignees: Named[]
  companies: Named[]
  /** Aplicatiile de business din firmele omului; lipsa sau gol = caseta nu se deseneaza. */
  applications?: Named[]
}

export const EMPTY_FILTER_OPTIONS: TicketFilterOptions = {
  types: [],
  statuses: [],
  priorities: [],
  categories: [],
  subcategories: [],
  groups: [],
  assignees: [],
  companies: [],
}

/** Aceiasi parametri pe care ii trimitea DataTables + `$.extend(d, TicketFilters)`. */
export function ticketTableParams(r: TicketTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)

  for (const key of ID_FILTER_KEYS) {
    for (const value of r.filters[key]) p.append(`${key}[]`, value)
  }
  if (r.filters.advanced !== '') p.set('advanced', r.filters.advanced)
  if (r.filters.filterFrom !== '') p.set('filterFrom', r.filters.filterFrom)
  if (r.filters.filterTo !== '') p.set('filterTo', r.filters.filterTo)
  if (r.filters.taken_by_adam) p.set('taken_by_adam', '1')

  return p
}

function tablePath(list: TicketListName): string {
  return list === 'closed_tickets' ? '/easeedesk/closed-tickets-table' : '/easeedesk/tickets-table'
}

export function ticketPageQuery(r: TicketTableRequest) {
  return queryOptions({
    queryKey: ['easeedesk', 'table', r],
    queryFn: () => apiGet<TicketTablePage>(`${tablePath(r.list)}?${ticketTableParams(r).toString()}`),
  })
}

export const ticketFilterOptionsQuery = queryOptions({
  queryKey: ['easeedesk', 'filter-options'],
  queryFn: () => apiGet<TicketFilterOptions>('/easeedesk/ticket-filters'),
  staleTime: 5 * 60 * 1000,
})

/** Un filtru salvat - randul din it_easeedesk_quick_filters, cu `filter` desfacut. */
export interface SavedFilter {
  ID: number
  filter_name: string
  filter_type: 'default' | 'custom'
  filter: Record<string, unknown>
}

export type SavedFilterKind = 'active_tickets' | 'closed_tickets'

export function savedKindOf(list: TicketListName): SavedFilterKind {
  return list === 'closed_tickets' ? 'closed_tickets' : 'active_tickets'
}

export function savedFiltersQuery(kind: SavedFilterKind) {
  return queryOptions({
    queryKey: ['easeedesk', 'quick-filters', kind],
    queryFn: () => apiGet<SavedFilter[]>(`/easeedesk/quick-filters?type=${kind}`),
  })
}

export function saveQuickFilter(name: string, kind: SavedFilterKind, filters: TicketFilters): Promise<{ id: number }> {
  const fields: Record<string, string | string[] | boolean> = {
    'filter-name': name,
    'filter-type': kind,
  }
  for (const key of ID_FILTER_KEYS) {
    if (filters[key].length > 0) fields[`${key}[]`] = filters[key]
  }
  if (filters.advanced !== '') fields.advanced = filters.advanced
  if (filters.filterFrom !== '') fields.filterFrom = filters.filterFrom
  if (filters.filterTo !== '') fields.filterTo = filters.filterTo
  if (filters.taken_by_adam) fields.taken_by_adam = '1'

  return apiPost<{ id: number }>('/easeedesk/quick-filters', fields)
}

export function removeQuickFilter(id: number): Promise<{ removed: boolean }> {
  return apiPost<{ removed: boolean }>('/easeedesk/quick-filters/remove', { filter: id })
}

/**
 * Filtrele PUSE ACUM pe lista, tinute minte intre pagini
 * (`easeedesk_filters:<UserID>` din Redis). Cheia nu se mai trimite: o face
 * serverul din omul logat.
 */
export const currentFiltersQuery = queryOptions({
  queryKey: ['easeedesk', 'user-filters'],
  queryFn: () => apiGet<Record<string, unknown>>('/easeedesk/user-filters'),
  staleTime: Infinity,
})

export function saveCurrentFilters(filters: TicketFilters): Promise<{ saved: boolean }> {
  const fields: Record<string, string | string[]> = {}
  for (const key of ID_FILTER_KEYS) fields[`data[${key}]`] = filters[key]
  fields['data[advanced]'] = filters.advanced
  fields['data[filterFrom]'] = filters.filterFrom
  fields['data[filterTo]'] = filters.filterTo
  fields['data[taken_by_adam]'] = filters.taken_by_adam ? '1' : ''

  return apiPost<{ saved: boolean }>('/easeedesk/user-filters', fields)
}

/** Ce a fost tinut minte, inapoi in forma paginii. */
export function filtersFromStored(stored: Record<string, unknown> | undefined): TicketFilters {
  if (!stored) return EMPTY_TICKET_FILTERS

  const out: TicketFilters = { ...EMPTY_TICKET_FILTERS }
  for (const key of ID_FILTER_KEYS) {
    const value = stored[key]
    if (Array.isArray(value)) out[key] = value.map((v) => String(v)).filter((v) => v !== '')
    else if (typeof value === 'string' && value !== '') out[key] = [value]
  }
  if (typeof stored.advanced === 'string') out.advanced = stored.advanced
  if (typeof stored.filterFrom === 'string') out.filterFrom = stored.filterFrom
  if (typeof stored.filterTo === 'string') out.filterTo = stored.filterTo
  out.taken_by_adam = stored.taken_by_adam === '1' || stored.taken_by_adam === 1 || stored.taken_by_adam === true

  return out
}

/** Cate filtre sunt puse - cifra de pe butonul de filtre. */
export function activeFilterCount(filters: TicketFilters): number {
  let count = 0
  for (const key of ID_FILTER_KEYS) if (filters[key].length > 0) count++
  if (filters.advanced !== '') count++
  if (filters.filterFrom !== '') count++
  if (filters.filterTo !== '') count++
  if (filters.taken_by_adam) count++
  return count
}

/**
 * Contractul fisei unui tichet Easeedesk - perechea lui `Easeedesk::ticket` +
 * `ticketData` + `expandTicket` din portal, plus filele care se cer dupa ce
 * fisa e pe ecran.
 *
 * Carcasa vine intr-un singur raspuns. In portalul vechi erau patru drumuri
 * unul dupa altul: pagina desena un `<div class="ticketTable">` gol, care cerea
 * `ticketTable`, care desena un tabel gol, care cerea `ticketData` pentru randul
 * de sus si abia apoi `expandTicket` pentru filele de dedesubt.
 */

export interface TicketMark {
  kind: 'chat' | 'conversation' | 'comments'
  count: number
}

export interface TicketCardRow {
  id: number
  subject: string
  type: { name: string; short: string; color: string }
  mark: TicketMark | null
  attachments: number
  company: string
  office: string
  affected: { category: number; text: string }
  priority: { id: number; name: string; color: string }
  category: string
  sla: { seconds: number; activeSeconds: number; breached: boolean }
  /** SLA dupa profil (calendar de lucru); null / lipsa = fara profil, ca azi. */
  slaClock?: TicketSlaClock | null
  creator: string
  source: { icon: string; color: string; name: string }
  group: string
  assignee: { name: string; takeOver: string }
  created: string
  updated: string
  status: { name: string; color: string }
  closed: boolean
  counts: { chat: number; conversation: number; comments: number }
}

export interface TicketTab {
  id: string
  label: string
  /** Adresa din portal, fara numarul tichetului: `easeedesk/ticketSummary/`. E si cheia filei. */
  href: string
  color?: string
  live?: boolean
  badge: { color: string; id: string; count: string } | null
}

export interface TicketBrief {
  id: number
  subject: string
  status: string
  color: string
  closed: boolean
}

export interface TicketProblem {
  id: number
  title: string
  status: number
  statusLabel: string
  color: string
  workaround: string
}

export interface TicketCard {
  id: number
  closed: boolean
  /** Tichetul unui incident XDR: fisa iese in evidenta (chenar rosu) si se deschide pe fila "XDR". */
  xdr?: boolean
  plan?: { id: number; launchable: number; state: string; on_parent?: { ticket_id: number; plan_id: number } } | null
  row: TicketCardRow | null
  family: {
    parent: TicketBrief | null
    children: TicketBrief[]
    /** Change-urile care au cauzat incidentul asta (legatura "cauzat de change"). */
    causedBy?: TicketBrief[]
    /** Incidentele cauzate de change-ul asta. */
    caused?: TicketBrief[]
  }
  problems: TicketProblem[]
  tabs: { details: TicketTab[]; actions: TicketTab[] }
  badges: Record<string, number>
}

export interface TicketLogEntry {
  kind: 'change' | 'log' | 'syslog'
  at: string
  device: string
  group: string
  type?: string
  field?: string
  value?: string
  authorized?: boolean
  window?: string
  ticketId?: number
  category?: string
  object?: string
  title?: string
  message?: string
}

export interface TicketLogsPage {
  ticket: number
  days: number
  periods: { days: number; label: string }[]
  devices: { key: string; name: string; count: number }[]
  entries: TicketLogEntry[]
}

export interface TicketSubordinatesPage {
  ticket: number
  parent: { id: number; subject: string } | null
  children: { id: number; subject: string; color: string }[]
  candidates: { id: number; subject: string; note: string }[]
}

export interface TicketLicense {
  status?: string
  licenseType?: string
  remaining_days?: number
  end?: string
}

export interface TicketDeviceBox {
  id: number
  name: string
  lastActivity: string
  seconds: number | null
  live: boolean
  agentVersion: string
  forceUpdateVersion: string | null
  owner: string
  email: string
  license: TicketLicense
}

export interface TicketDevicesPage {
  empty: boolean
  affected: { type: string; name: string; license: TicketLicense; devices: TicketDeviceBox[] }
  licenseChip: { tone: string; text: string } | null
}

/**
 * Capul filei "Summary": "User Details" si "Current Request".
 *
 * Masinile NU sunt aici - raman la `/devices`, cerute separat dupa ce fisa e pe
 * ecran, ca in portal.
 */
export interface TicketSummaryPage {
  ticket: number
  subject: string
  /** `getTicketCreator(...)` - randul "Customer Name" (in portal "WO Customer Name"). */
  creator: string
  affected: { type: string; name: string; license: TicketLicense; devices: TicketDeviceBox[] }
  /** Managerul de cont al firmei (`firma.AM`), randul "IT Manager". */
  manager: { id: number; name: string } | null
  /** De cate ori si-a schimbat tichetul operatorul. */
  assigneeChanges: number
  /** Tehnicienii firmei; macheta veche arata numai primul, cu "Show more ...". */
  technicians: { id: number; name: string }[]
  /** Cifra din cutia din dreapta. */
  lastWeekRequests: number
  /** HTML de editor bogat; se deseneaza intr-un cadru inchis, nu in pagina. */
  description: string
  /**
   * Drumul cererii (`_stages.php`), NUMAI pentru tichetele din catalogul de
   * servicii; null pentru celelalte, si atunci nu se deseneaza nimic.
   */
  stages: TicketStagesBand | null
  /** Tichetul e din arhiva. */
  closed: boolean
  /* Cele sapte blocuri de dedesubt, in ordinea machetei - vezi `TicketSummaryBlocks.tsx`. */
  /** "Last Updates": ultimele doua comentarii vizibile omului; textul cu `<br>` facut rand nou. */
  comments: { name: string; created: string; comment: string }[]
  /** Pozele (`is_image > 0`), cu adresele de pe serverul de descarcari (goale fara DOWNLOAD_SERVER). */
  images: TicketSummaryFile[]
  /** Fisierele (`is_image = 0`). */
  files: TicketSummaryFile[]
  subscribers: string[]
  /** "Operations Summary" - bara SLA (null fara `sla_time`) si randurile pasilor activi. */
  operations: {
    sla: { label: string; percent: number; tone: string } | null
    rows: { name: string; started: string; message: string; tone: string; estimated: string; percent: number | null; firstStep: boolean }[]
  }
  /** "Times of Operations", gata formatate (`d M, H:i`); gol = nu se deseneaza. */
  times: { deliveryTime: string; created: string; updated: string; duedate: string; estimatedEffort: string; estimatedIntervention: string; resolved: string }
  customFields: { label: string; values: string[] }[]
  feedback: { rating: number; comments: string; sender: string; created: string } | null
  /** Motivul ultimei escaladari (tip > 1); macheta ecranului nu-l scria, numai emailul. */
  escalationReason: string | null
}

export interface TicketSummaryFile {
  id: number
  name: string
  size: number
  /** De aratat (miniatura, lightbox). */
  url: string
  thumbnail: string
  /** De descarcat. */
  download: string
}

/** Un stadiu al fluxului; `type` e `status_type` (1 activ, 2 rezolvat, 3 final). */
export interface TicketStage {
  status_id: number
  name: string
  colour: string | null
  type: number
}

/** `TicketStages::forTicket()` - numele fluxului, stadiile in ordinea in care se ajunge la ele, si statusul de acum. */
export interface TicketStagesBand {
  name: string
  stages: TicketStage[]
  current: number
}

/**
 * Un rand din fila "History", in cele trei feluri ale lui.
 *
 * `values` e o schimbare obisnuita - de la ce, la ce. `comment` si `email` sunt
 * texte lungi, care se deschid; `ref` e cheia dupa care se cere textul din
 * portal. Cand `ref` lipseste la un email, rezumatul e chiar tot mesajul.
 */
export interface TicketChangeRow {
  field: string
  title: string
  kind: 'values' | 'comment' | 'email'
  old: string[]
  new: string[]
  ref: string | null
}

export interface TicketChangeEvent {
  id: number
  action: string
  icon: string
  color: string
  author: string
  at: string
  /**
   * Numai pe escaladari: de unde a plecat tichetul, la cine, si felul
   * escaladarii. Oricare poate lipsi - o escaladare veche nu-si stie grupul
   * de dinainte. Lipseste cu totul pe raspunsurile de dinainte de 21 oct 2026.
   */
  escalation?: { from: string | null; to: string | null; type: string | null } | null
  changes: TicketChangeRow[]
}

export interface TicketChangesPage {
  ticket: number
  filter: string
  filters: { key: string; label: string }[]
  events: TicketChangeEvent[]
}

export interface TicketRelatedRow {
  id: number
  subject: string
  creator: string
  type: string
  status: string
  resolved: string | null
  assignee: string
}

export interface TicketRelatedPage {
  ticket: number
  groups: { kind: 'user' | 'device'; name: string; tickets: TicketRelatedRow[] }[]
}

export interface TicketAffectedItem {
  ciId: number | null
  name: string
  className: string
  companyName: string
  ownerName: string
  isDevice: boolean
  deviceId: number | null
  outsideCmdb: boolean
  status: string
  criticality: string
  health: string
  healthTone: string
  impactCount: number
  origin: string
  recordedAt: string
  /** Se poate scoate de aici: numai ce detine `it_easeedesk_ticket_affected_ci`, nu oglinda unui aparat. */
  removable: boolean
}

export interface TicketAffectedItemsPage {
  ticket: number
  closed: boolean
  editable: boolean
  window: { scopeType: number; scopeWord: string; scopeName: string; scopeId: number; companyId: number; name: string; itemCount: number } | null
  items: TicketAffectedItem[]
}

export interface TicketPerson {
  id: number
  name: string
  phone: string
  email: string
  department: string
  /** Grupul omului - `data-group` al butonului Escalate; 0 la contacte si manager. */
  groupId?: number
  image: string
  oncallPhone?: string
  tickets?: number
}

export interface TicketContactsPage {
  ticket: number
  /** Tichetul e deschis: numai atunci se deseneaza "Escalate". */
  open: boolean
  companyName: string
  departments: { name: string; people: TicketPerson[] }[]
  oncall: TicketPerson[]
  manager: TicketPerson | null
  critical: TicketPerson[]
  vips: TicketPerson[]
}

export interface TicketDeviceTabPage {
  ticket: number
  tab: string
  devices: { id: number; name: string }[]
}

/** Un buton al panoului "Change" - `Ticket_details_settings::getAllActionTabs()`. */
export interface TicketActionButton {
  id: string
  label: string
  href: string
  /** `/0`, `/1`, ... - cele trei escaladari deschid acelasi formular. */
  tail: string
}

/**
 * Ce arata butonul "Advance", dupa `Easeedesk::checkTicket()`.
 *
 * `takeover` - preluarea (tichetul nu e al lui si fluxul are butonul);
 * `escalated` / `planned` / `reopen` / `new-ticket` - celelalte panouri pe care
 * portalul le pune IN LOCUL formularului; `form` - chiar formularul de avansare.
 */
export type TicketAdvanceKind = 'takeover' | 'escalated' | 'planned' | 'reopen' | 'new-ticket' | 'form'

export interface TicketAdvance {
  kind: TicketAdvanceKind
  /** Cine il are acum; `id = 0` inseamna neatribuit. Numele vine doar la `takeover`. */
  assignee: { id: number; name: string }
}

export interface TicketActionsPage {
  ticket: number
  resolved: boolean
  buttons: TicketActionButton[]
  advance: TicketAdvance
}

/* ---------------------------------------------------------------------- */
/*  Cele trei istorii de mesaje: emailuri, chat, SMS                       */
/* ---------------------------------------------------------------------- */

export interface TicketEmail {
  id: number
  subject: string
  sender: string
  to: string[]
  cc: string[]
  kind: 'sent' | 'received'
  unread: boolean
  trash: boolean
  at: string
  /** HTML scris de altcineva - se deseneaza numai intr-un cadru inchis. */
  body: string
  attachments: { name: string; size: number }[]
}

export interface TicketConversationPage {
  ticket: number
  folder: string
  folders: { key: string; label: string }[]
  messages: TicketEmail[]
}

export interface TicketChatMessage {
  id: number
  author: string
  kind: 'agent' | 'customer'
  message: string
  at: string
  seen: boolean
}

export interface TicketChatPage {
  ticket: number
  messages: TicketChatMessage[]
  history: { ticket: number; at: string }[]
}

export interface TicketSmsMessage {
  id: number
  sender: string
  phone: string
  message: string
  kind: 'sent' | 'received'
  at: string
}

export interface TicketSmsPage {
  ticket: number
  messages: TicketSmsMessage[]
}

/* ---------------------------------------------------------------------- */
/*  Cele doua file de acces la distanta                                    */
/* ---------------------------------------------------------------------- */

export interface TicketRemoteDevice {
  id: number
  name: string
  type: number
  loggedUser: string
  lastActivity: string
  ownerId: number
  inOffice: boolean
  askConsent: boolean
}

export interface TicketRemoteLinuxDevice {
  id: number
  name: string
  type: number
  loggedUser: string
  lastActivity: string
  ip: string
  ownerId: number
}

export interface TicketRemoteConnectPage {
  ticket: number
  /** `rustdesk` sau `screenconnect` - unealta aprinsa la furnizor. */
  tool: string
  affectedOwner: number | null
  devices: TicketRemoteDevice[]
  linuxDevices: TicketRemoteLinuxDevice[]
}

export interface TicketRemoteControlPage {
  ticket: number
  subcategory: string
  tabs: { id: string; label: string; href: string }[]
  devices: { id: number; name: string; lastActivity: string }[]
}

/* ---------------------------------------------------------------------- */
/*  Manuals si Standards                                                   */
/* ---------------------------------------------------------------------- */

export interface TicketManualsPage {
  ticket: number
  images: { id: number; name: string; size: number; url: string }[]
}

export interface TicketPolicyContent {
  subcategory_id: number
  scope: string
  policy_id: number
  standard: string
  configuration: string
  exception: string
  attachments: { id: number; file_name: string; url: string }[]
}

export interface TicketStandardsPage {
  ticket: number
  categories: { id: number; name: string }[]
  subcategories: { id: number; name: string }[]
  categoryId: number
  subcategoryId: number
  scope: string
  content: TicketPolicyContent | null
}

/* ---------------------------------------------------------------------- */
/*  Pass Safe                                                              */
/* ---------------------------------------------------------------------- */

/** Un rand de parola: cine si pentru ce, niciodata CE. */
export interface TicketPasswordEntry {
  id: number
  description: string
  user: string
  device: string
}

export interface TicketPassSafePage {
  ticket: number
  deviceTypes: { id: number; name: string }[]
  deviceTypeId: number
  ticketPasswords: TicketPasswordEntry[]
  ownerPasswords: TicketPasswordEntry[]
  devicePasswords: TicketPasswordEntry[]
}

/* ---------------------------------------------------------------------- */
/*  Contract                                                               */
/* ---------------------------------------------------------------------- */

export interface TicketContractService {
  id: number
  name: string
  description: string
  category: string
  coverage: string
  interventionSla: string
  quantity: string
  /** Acopera serviciul asta chiar tipul tichetului? Coloana "Has Services". */
  covers: boolean
}

export interface TicketContractRow {
  id: number
  number: string
  description: string
  type: string
  startsAt: string
  endsAt: string
  services: TicketContractService[]
}

export interface TicketContractPage {
  ticket: number
  ticketType: number
  contracts: TicketContractRow[]
  domains: string[]
  inactiveModules: { name: string; icon: string }[]
}

/* ---------------------------------------------------------------------- */
/*  Fast Dispatch                                                          */
/* ---------------------------------------------------------------------- */

export interface TicketDispatchPerson {
  id: number
  name: string
  phone: string
  email: string
  department: string
  /** Grupul omului - `data-group` al butonului Escalate. */
  groupId: number
  image: string
  distance: number
  openTickets: number
  resolvedTickets: number
  company: string
  office: string
  /** "Checked-in" / "Commuting to", gol pe lista cu toti. */
  state: string
  fixedLocation: boolean
  collaborator: boolean
}

export interface TicketDispatchPage {
  ticket: number
  officeId: number
  officeName: string
  latitude: number | null
  longitude: number | null
  offices: { id: number; name: string }[]
  settings: { maxDistance: number; checkIn: boolean; commuting: boolean; escalate: boolean }
  everyone: boolean
  /** Tichetul e deschis: numai atunci se deseneaza "Escalate". */
  open: boolean
  bands: { name: string; people: TicketDispatchPerson[] }[]
}

export interface TicketCommentFormPage {
  ticket: number
  /**
   * Cum arata fila, hotarat de server: `open` casuta obisnuita, `reopen`
   * casuta cu avertismentul ca un comentariu redeschide tichetul, `closed`
   * numai instiintarea - un tichet terminat nu mai primeste comentarii.
   */
  mode: 'open' | 'reopen' | 'closed'
  notice: string
  canComment: boolean
  action: string
  label: string
  required: boolean
  submit: string
  attach: string
  checkboxes: { name: string; label: string }[]
  /**
   * Fila "Collaborate" (`PComments.buildCommentForm()`): butoanele radio de
   * vizibilitate (3 Internal, 2 Ticket Info, 1 Sent to Agent), felurile
   * proprii de comentariu ale furnizorului si sabloanele
   * (`collaborateTemplateLists`). Fereastra din lista nu le deseneaza.
   */
  visibilities: { value: number; label: string; hint: string }[]
  types: { value: number; name: string; color: string }[]
  templates: { id: number; title: string; message: string }[]
}

/** Un comentariu din fila "Collaborate" - `Easeedesk::commentsLists`. */
export interface TicketComment {
  id: number
  /** Comentariul la care raspunde, sau null pe un fir nou. */
  parent: number | null
  type: number
  typeName: string
  typeColor: string
  /** 1 public (trimis clientului), 2 privat (numai pe tichet), 3 intern. */
  visibility: number
  author: string
  /** HTML, cu `<br />` din salvare - se deseneaza ca HTML, ca in `PComments`. */
  comment: string
  unread: boolean
  requestFiles: boolean
  created: string
}

export interface TicketCommentsPage {
  ticket: number
  /** Cele mai noi primele, cu raspunsurile amestecate printre firele lor. */
  comments: TicketComment[]
}

/*
 * Calea se scrie FARA `/api/v2`: `apiGet` il pune el, o singura data
 * (`api/client.ts`, `fetch(`/api/v2${path}`)`).
 *
 * Aici statea `'/api/v2/easeedesk/tickets'`, deci fiecare din cele cinci
 * cereri ale fisei pleca spre `/api/v2/api/v2/easeedesk/tickets/...` - o
 * adresa care nu e ruta nicaieri. Fisa unui tichet nu se deschidea deloc: se
 * vedea numai chenarul rosu de eroare. Celelalte trei benzi Easeedesk
 * (`easeedeskBoards.ts`, `settingsEaseedeskCategories.ts`, listele de mai sus)
 * scriu calea relativ, si de-aia mergeau.
 *
 * Se exporta ca sa poata fi incuiat de proba: `easeedesk.test.ts` cere ca
 * nicio cale de-aici sa nu inceapa cu `/api`.
 */
export const TICKET_BASE = '/easeedesk/tickets'

const BASE = TICKET_BASE

export function ticketQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id],
    queryFn: () => apiGet<TicketCard>(`${BASE}/${id}`),
  })
}

export function ticketLogsQuery(id: number, days: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'logs', days],
    queryFn: () => apiGet<TicketLogsPage>(`${BASE}/${id}/logs?days=${days}`),
  })
}

export function ticketSubordinatesQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'subordinates'],
    queryFn: () => apiGet<TicketSubordinatesPage>(`${BASE}/${id}/subordinates`),
  })
}

export function ticketChangesQuery(id: number, filter: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'changes', filter],
    queryFn: () => apiGet<TicketChangesPage>(`${BASE}/${id}/changes?filter=${encodeURIComponent(filter)}`),
  })
}

/**
 * Textul unui comentariu din fila History - `easeedesk/getTicketCommentDetails/{c}`.
 * Capatul pazeste ca la fila: dreptul `easeedesk.tickets.read`, tichetul
 * firmei (404 altfel). Un comentariu privat vine intreg, ca in lista de
 * comentarii a tichetului: "privat" inseamna doar ca nu pleaca la client.
 */
export function fetchTicketComment(comment: number): Promise<{ id: number; ticket: number; comment: string; created: string }> {
  return apiGet(`/easeedesk/comments/${comment}`)
}

export function ticketRelatedQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'related'],
    queryFn: () => apiGet<TicketRelatedPage>(`${BASE}/${id}/related`),
  })
}

export function ticketAffectedItemsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'affected-items'],
    queryFn: () => apiGet<TicketAffectedItemsPage>(`${BASE}/${id}/affected-items`),
  })
}

export function ticketContactsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'contacts'],
    queryFn: () => apiGet<TicketContactsPage>(`${BASE}/${id}/contacts`),
  })
}

export function ticketDeviceTabQuery(id: number, tab: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'device-tab', tab],
    queryFn: () => apiGet<TicketDeviceTabPage>(`${BASE}/${id}/device-tab/${tab}`),
  })
}

export function ticketDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'devices'],
    queryFn: () => apiGet<TicketDevicesPage>(`${BASE}/${id}/devices`),
  })
}

export function ticketSummaryQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'summary'],
    queryFn: () => apiGet<TicketSummaryPage>(`${BASE}/${id}/summary`),
  })
}

export function ticketActionsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'actions'],
    queryFn: () => apiGet<TicketActionsPage>(`${BASE}/${id}/actions`),
  })
}

export function ticketConversationQuery(id: number, folder: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'conversation', folder],
    queryFn: () => apiGet<TicketConversationPage>(`${BASE}/${id}/conversation?folder=${encodeURIComponent(folder)}`),
  })
}

/* --- Conversation: scrierile (`Conversation.php`) --------------------------- */

/** `newEmailData` / `replyEmailData`: ce stie formularul inainte sa scrie. */
export interface TicketEmailCompose {
  ticket: number
  contacts: { name: string; email: string }[]
  emailTo: string[]
  emailCc: string[]
  emailBody: string
  emailSubject: string
  /** Emailul la care se raspunde, sau null la unul nou. */
  replyTo: number | null
}

export function ticketEmailComposeQuery(id: number, replyTo: number | null) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'conversation', 'compose', replyTo],
    queryFn: () => apiGet<TicketEmailCompose>(replyTo === null ? `${BASE}/${id}/conversation/new` : `${BASE}/${id}/conversation/${replyTo}/reply`),
    staleTime: 0,
    gcTime: 0,
  })
}

export interface TicketEmailSendReply {
  status: string
  id: number
  sent: number
  recipients: number
  files: number
}

/** `conversation/newEmail` si `replyEmail`: acelasi cap, `reply_to` la raspuns. */
export function sendTicketEmail(
  id: number,
  fields: { to: string[]; cc: string[]; subject: string; message: string; replyTo: number | null; files: File[] },
): Promise<TicketEmailSendReply> {
  const body = new FormData()
  fields.to.forEach((one) => body.append('emailTo[]', one))
  fields.cc.forEach((one) => body.append('emailCc[]', one))
  body.append('emailSubject', fields.subject)
  body.append('emailMessage', fields.message)
  if (fields.replyTo !== null) body.append('reply_to', String(fields.replyTo))
  fields.files.forEach((file) => body.append('files[]', file, file.name))
  return apiPostForm<TicketEmailSendReply>(`${BASE}/${id}/conversation`, body)
}

/** `conversation/markAsRead` - la deschiderea unui email primit. */
export function markTicketEmailRead(id: number, email: number): Promise<{ status: string }> {
  return apiPost<{ status: string }>(`${BASE}/${id}/conversation/${email}/read`, {})
}

/** `conversation/moveToTrash`. */
export function trashTicketEmail(id: number, email: number): Promise<{ status: string }> {
  return apiPost<{ status: string }>(`${BASE}/${id}/conversation/${email}/trash`, {})
}

export function ticketChatQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'chat'],
    queryFn: () => apiGet<TicketChatPage>(`${BASE}/${id}/chat`),
  })
}

export interface TicketChatSendReply {
  status: string
  id: number
  message: string
  at: string
  /** Primul mesaj al discutiei: aparatul primeste si fereastra de chat. */
  first: boolean
}

/** `chat_conversation/sendMessage`: mesajul operatorului, `ACH` catre aparat. */
export function sendTicketChat(id: number, message: string): Promise<TicketChatSendReply> {
  return apiPost<TicketChatSendReply>(`${BASE}/${id}/chat`, { message })
}

export function ticketSmsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'sms'],
    queryFn: () => apiGet<TicketSmsPage>(`${BASE}/${id}/sms`),
  })
}

export function ticketRemoteConnectQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'remote-connect'],
    queryFn: () => apiGet<TicketRemoteConnectPage>(`${BASE}/${id}/remote-connect`),
  })
}

export function ticketRemoteControlQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'remote-control'],
    queryFn: () => apiGet<TicketRemoteControlPage>(`${BASE}/${id}/remote-control`),
  })
}

export function ticketManualsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'manuals'],
    queryFn: () => apiGet<TicketManualsPage>(`${BASE}/${id}/manuals`),
  })
}

export function ticketStandardsQuery(id: number, category: number, subcategory: number, scope: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'standards', category, subcategory, scope],
    queryFn: () =>
      apiGet<TicketStandardsPage>(
        `${BASE}/${id}/standards?category=${category}&subcategory=${subcategory}&scope=${encodeURIComponent(scope)}`
      ),
  })
}

export function ticketPassSafeQuery(id: number, deviceType: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'pass-safe', deviceType],
    queryFn: () => apiGet<TicketPassSafePage>(`${BASE}/${id}/pass-safe?deviceType=${deviceType}`),
  })
}

export function ticketContractQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'contract'],
    queryFn: () => apiGet<TicketContractPage>(`${BASE}/${id}/contract`),
  })
}

export function ticketDispatchQuery(id: number, office: number, everyone: boolean) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'fast-dispatch', office, everyone],
    queryFn: () =>
      apiGet<TicketDispatchPage>(`${BASE}/${id}/fast-dispatch?office=${office}&all=${everyone ? 1 : 0}`),
  })
}

export function ticketCommentFormQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'comment-form'],
    queryFn: () => apiGet<TicketCommentFormPage>(`${BASE}/${id}/comment-form`),
  })
}

/** Lista comentariilor - `easeedesk/commentsLists/{id}`. */
export function ticketCommentsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'comments'],
    queryFn: () => apiGet<TicketCommentsPage>(`${BASE}/${id}/comments`),
  })
}

/**
 * Marcarea ca citite - partea de scriere a lui `easeedesk/ticketComments`.
 * Fila o cere dupa ce a aratat lista; in portal se facea din chiar GET-ul ei.
 */
export function markTicketCommentsRead(id: number): Promise<{ status: string }> {
  return apiPost<{ status: string }>(`${BASE}/${id}/comments/read`, {})
}

/**
 * Fila ceruta prin adresa: `/app/easeedesk/ticket/123/summary-tab`.
 *
 * In portal, al doilea argument al lui `Easeedesk::ticket()` era chiar id-ul
 * elementului de fila, iar macheta il apasa cu javascript dupa ce randul extins
 * ajungea pe ecran. Aici nu se mai asteapta nimeni: fila ceruta e cea deschisa
 * din prima.
 */
export function startingTab(wanted: string | undefined, tabs: TicketTab[]): string {
  if (wanted) {
    const hit = tabs.find((t) => t.id === wanted)
    if (hit) return hit.href
  }
  return tabs.length > 0 ? tabs[0].href : ''
}

/* ------------------------------------------------------------------------ */
/*  SCRIERILE de pe lista si de pe fisa                                      */
/*                                                                           */
/*  Perechile vechi: `easeedesk/takeoverTicket`, `easeedesk/addComment`,     */
/*  `easeedesk/delete` si `easeedesk/delete_tickets`. Calea se scrie FARA    */
/*  `/api/v2` - il pune `apiPost` (vezi comentariul de la TICKET_BASE).      */
/* ------------------------------------------------------------------------ */

/** Preluarea: fara raspuns `confirm`, tichetul e al tau. */
export interface TakeOverReply {
  status?: string
  /** 1 = tichetul are deja operator; se intreaba omul si se trimite iar, cu `force`. */
  confirm?: number
}

export function takeOverTicket(id: number, force = false): Promise<TakeOverReply> {
  return apiPost<TakeOverReply>(`${BASE}/${id}/take-over`, force ? { force: '1' } : {})
}

export interface DeskRunRow {
  id: number
  state: string
  rounds: number
  outcome: string
  trust_level: number | null
  cost_usd: number
  tokens_in: number
  tokens_out: number
  cancel_requested: number
  failure_kind: string
  conversation_id: number
  running: boolean
  ai_started_at: string
  updated_at: string
}

export function deskRunQuery(id: number) {
  return {
    queryKey: ['easeedesk', 'ticket', id, 'desk'] as const,
    queryFn: (): Promise<{ run: DeskRunRow | null }> => apiGet(`${BASE}/${id}/desk`),
  }
}

export function stopDeskRun(id: number): Promise<{ ok: boolean; stopped: boolean }> {
  return apiPost(`${BASE}/${id}/desk/stop`, {})
}

/**
 * Trecerea la alta echipa - butonul "Transfer to" de langa Takeover.
 *
 * In portal e un x-editable pe un `<a>`: se apasa, se alege echipa dintr-o
 * lista adusa de la `/easeedesk/groups`, si se salveaza. Aici lista vine din
 * `ticketFilterOptionsQuery` (aceleasi echipe, deja in cache de la bara de
 * filtre), iar salvarea e postul asta.
 */
export function transferTicket(id: number, group: number): Promise<{ status: string; ticket: number; group: number }> {
  return apiPost(`${BASE}/${id}/transfer`, { group: String(group) })
}

export function trashTicket(id: number): Promise<{ status: string }> {
  return apiPost<{ status: string }>(`${BASE}/${id}/trash`, {})
}

export interface BulkTrashReply {
  status: string
  deleted: number
  skipped: number
}

export function trashTickets(ids: number[]): Promise<BulkTrashReply> {
  return apiPost<BulkTrashReply>(`${BASE}/trash`, { tickets: ids })
}

/* -------------------------------------------------------------------------- */
/*  ACTIUNILE IN LOT - Merge, Suspend service, Assign to Operator.            */
/*  Ferestrele vechi: `merge_form`, `multiselectSuspendServiceForm`,          */
/*  `assign_operator_form`; scrierile: `merge_tickets`, `multiselectSuspend-  */
/*  Service`, `assign_tickets_to_operator`.                                   */
/* -------------------------------------------------------------------------- */

export type BulkFormKind = 'merge' | 'assign' | 'suspend'

export interface BulkFormTicket {
  ID: number
  subject: string
}

/** Ce afla fereastra inainte sa scrie - tichetele bifate, erorile, listele. */
export interface BulkForm {
  kind: BulkFormKind
  /** Erorile portalului, cu textul lor: unele opresc fereastra, altele doar avertizeaza. */
  errors: string[]
  tickets: BulkFormTicket[]
  /** Merge: parintii posibili (aceeasi firma, acelasi tip, nerezolvati). */
  available?: BulkFormTicket[]
  /** Assign: operatorii firmei furnizorului. */
  assignees?: Array<{ value: number; name: string }>
}

export function bulkFormQuery(kind: BulkFormKind, ids: number[]) {
  return queryOptions({
    queryKey: ['easeedesk', 'bulk-form', kind, ids],
    queryFn: () => apiGet<BulkForm>(`${BASE}/bulk-form/${kind}?tickets=${ids.join(',')}`),
    staleTime: 0,
  })
}

export interface MergeReply {
  status: string
  ticket: number
  merged: number[]
}

/** `merge_tickets`: copiii intra in parinte; `merge_fields` poarta `description` cand se lipesc si descrierile. */
export function mergeTickets(parent: number, ids: number[], subject: string, mergeDescription: boolean): Promise<MergeReply> {
  const body: Record<string, string | number[] | string[]> = {
    ticket_id: String(parent),
    ticket_subject: subject,
    tickets: ids,
  }
  if (mergeDescription) body.merge_fields = ['description']

  return apiPost<MergeReply>(`${BASE}/merge`, body)
}

export interface AssignReply {
  status: string
  assigned: number
  skipped: number
  reasons: string[]
}

export function assignTickets(ids: number[], assignee: number): Promise<AssignReply> {
  return apiPost<AssignReply>(`${BASE}/assign`, { tickets: ids, assignee: String(assignee) })
}

/**
 * Campurile machetei vechi `forms/commentForm.php` (fereastra din lista) si
 * ale formularului lui `PComments` (fila Collaborate): vizibilitatea ca numar,
 * felul propriu de comentariu, si fisierele `files[]`.
 */
export interface TicketCommentFields {
  comment: string
  informClient?: boolean
  requestFiles?: boolean
  parentComment?: number
  /** `comment_visibility`: 3 intern, 2 pe tichet, 1 trimis clientului. */
  visibility?: number
  /** `comment_type`: numarul unui fel propriu al furnizorului. */
  commentType?: number
  files?: File[]
}

export interface TicketCommentReply {
  status: string
  /** Numarul comentariului; lipseste cand comentariul a REDESCHIS tichetul rezolvat. */
  id?: number
  files?: number
  ticketStatus?: number
}

export function addTicketComment(id: number, fields: TicketCommentFields): Promise<TicketCommentReply> {
  const body: Record<string, string> = { comment: fields.comment }
  if (fields.informClient) body['inform-client'] = 'on'
  if (fields.requestFiles) body.request_files = 'on'
  if (fields.parentComment) body['parent-comment'] = String(fields.parentComment)
  if (fields.visibility) body.comment_visibility = String(fields.visibility)
  if (fields.commentType) body.comment_type = String(fields.commentType)

  /* Cu fisiere, formularul pleaca multipart, ca `enctype` din `PComments`. */
  if (fields.files && fields.files.length > 0) {
    const form = new FormData()
    for (const [key, value] of Object.entries(body)) form.append(key, value)
    for (const file of fields.files) form.append('files[]', file)

    return apiPostForm<TicketCommentReply>(`${BASE}/${id}/comment`, form)
  }

  return apiPost<TicketCommentReply>(`${BASE}/${id}/comment`, body)
}

/* -------------------------------------------------------------------------- */
/*  DESCHIDEREA UNUI TICHET NOU - butonul "New ticket" din lista.              */
/*  Perechea veche: `easeedesk/createTicket`.                                  */
/* -------------------------------------------------------------------------- */

/**
 * Campurile formularului de deschidere.
 *
 * Numele sunt cele din portal, fiindca tot ele ajung in `it_easeedesk_tickets`
 * si in tabelele de legatura. Formularul insusi e configurabil - fiecare
 * furnizor isi alege ce campuri are - deci `extra` poarta tot ce a desenat
 * schema: `ticket_category`, `assignee`, `customfield_12`, si asa mai departe.
 *
 * Ce NU se poate trimite de aici, fiindca le pune serverul: `ticket_source`,
 * `escalation_type`, `active_time`, `hold_time`, `taken_by_adam`. In portal
 * veneau din formular, si de acolo se putea naste un tichet care minte despre
 * ce e si de cand asteapta.
 */
export interface NewTicketFields {
  company: number
  subject: string
  description?: string
  /** Cand lipseste, serverul ia primul tip activ al furnizorului. */
  ticketType?: number
  /** Cand lipseste sau nu e un pas de plecare al fluxului, serverul ia primul. */
  ticketStatus?: number
  priority?: number
  office?: number
  assignee?: number
  assignedGroup?: number
  /** "user" | "equipment" | "ci" - impreuna cu `affected`. */
  affectedType?: 'user' | 'equipment' | 'ci'
  affected?: Array<number | string> | number | string
  affectedDevices?: number[]
  affectedUser?: number
  subscribers?: number[]
  notifiers?: number[]
  cause?: string
  resolution?: string
  updateComment?: string
  /** Restul campurilor pe care le cere schema formularului. */
  extra?: Record<string, string | string[]>
}

export interface NewTicketAnswer {
  status: string
  ticket: number
  ticketStatus: number
}

/** Dreptul BUTONULUI "New ticket" - `canAccess('easeedesk.tickets.create')` din `tickets/index.php`. */
export const CREATE_RIGHT = 'easeedesk.tickets.create'

/** Se arata butonul? Raspunde serverul, nu ghiceste ecranul. */
export const createRightQuery = queryOptions({
  queryKey: ['easeedesk', 'create-right'],
  queryFn: () => apiGet<{ can: Record<string, boolean> }>(`/me?can=${CREATE_RIGHT}`),
  staleTime: 5 * 60 * 1000,
})

/**
 * Descrierea formularului de tichet nou - `easeedesk/createTicketForm`.
 *
 * Aceeasi forma ca formularele filei "Change" (`TicketActionForm`), cu trei
 * campuri in plus: `type` (tipul ales sau primul activ), `company` (firma pe
 * care se deschide) si `status` (primul pas al fluxului). `blocked` spune de
 * ce nu se poate deschide, cu textul din portal.
 */
export interface TicketCreateFormPage {
  ticket: 0
  form: 'create'
  name: string
  submit: string
  type: number
  status: number
  company: number
  fields: TicketFormField[]
  blocked: { error: string; message: string } | null
}

export interface TicketCreateAsk {
  /** Tipul ales - formularul e al primului pas al fluxului lui. */
  type?: number
  /** Pasul de plecare ales, cand fluxul are mai multi. */
  status?: number
  /** Firma - punctele de lucru si contactele sunt ale ei. */
  company?: number
  /** Categoria - subcategoriile sunt ale ei. */
  category?: number
  /** Subcategoria - operatiile sunt ale ei. */
  subcategory?: number
  /** Echipa - operatorii sunt ai ei. */
  group?: number
}

/**
 * Se REINCARCA la fiecare alegere care schimba o lista, exact cum portalul
 * cerea tot formularul la schimbarea tipului (`newtype`) si cate un sertar la
 * celelalte (`getSubcategories`, `getGroupUsers`, `getCompanyOffices`).
 */
export function ticketCreateFormQuery(ask: TicketCreateAsk = {}) {
  const params = new URLSearchParams()
  for (const key of ['type', 'status', 'company', 'category', 'subcategory', 'group'] as const) {
    const value = ask[key]
    if (value) params.set(key, String(value))
  }
  const tail = params.toString()

  return queryOptions({
    queryKey: ['easeedesk', 'create-form', tail],
    queryFn: () => apiGet<TicketCreateFormPage>(`${BASE}/create-form${tail === '' ? '' : `?${tail}`}`),
    placeholderData: keepPreviousData,
  })
}

export type AffectedKind = 'user' | 'equipment' | 'ci'

export interface TicketAffectedSearch {
  company: number
  kind: AffectedKind
  options: TicketFormOption[]
}

/**
 * Sertarul de afectati, pe masura ce omul scrie - `easeedesk/get_field_options`
 * cu `field=ticket_affected`. Contactele cer un text; statiile si elementele
 * de configurare raspund si la gol.
 */
export function searchTicketAffected(company: number, kind: AffectedKind, q: string): Promise<TicketAffectedSearch> {
  const params = new URLSearchParams({ company: String(company), kind, q })

  return apiGet<TicketAffectedSearch>(`${BASE}/create-form/affected?${params.toString()}`)
}

/**
 * Un POST de formular care poate purta si fisiere - `ticket_attachments[]`,
 * ca `$_FILES['ticket_attachments']` in portal. Fara fisiere pleaca urlencoded,
 * ca pana acum; cu fisiere pleaca multipart, cu aceleasi campuri.
 */
function postForm<T>(path: string, body: Record<string, string | string[]>, files: File[] = []): Promise<T> {
  if (files.length === 0) return apiPost<T>(path, body)

  const form = new FormData()
  for (const [key, value] of Object.entries(body)) {
    if (Array.isArray(value)) for (const v of value) form.append(`${key}[]`, v)
    else form.append(key, value)
  }
  for (const file of files) form.append('ticket_attachments[]', file)

  return apiPostForm<T>(path, form)
}

/** "Quick Ticket" - `easeedesk/createQuickTicket`: firma, omul, subiect, descriere; restul din dispecerizarea sursei 22. */
export function createQuickTicket(fields: { ticket_company: string; ticket_affected: string; subject: string; description: string }): Promise<NewTicketAnswer> {
  return apiPost<NewTicketAnswer>(`${BASE}/quick`, { ...fields, affected_type: 'user' })
}

export function createTicket(fields: NewTicketFields, files: File[] = []): Promise<NewTicketAnswer> {
  const body: Record<string, string | string[]> = {
    ticket_company: String(fields.company),
    subject: fields.subject,
  }

  if (fields.description) body.description = fields.description
  if (fields.ticketType) body.ticket_type = String(fields.ticketType)
  if (fields.ticketStatus) body.ticket_status = String(fields.ticketStatus)
  if (fields.priority) body.ticket_priority = String(fields.priority)
  if (fields.office) body.ticket_office = String(fields.office)
  if (fields.assignee) body.assignee = String(fields.assignee)
  if (fields.assignedGroup) body.assigned_group = String(fields.assignedGroup)
  if (fields.affectedType) body.affected_type = fields.affectedType
  if (fields.affected !== undefined) {
    body.ticket_affected = Array.isArray(fields.affected)
      ? fields.affected.map(String)
      : String(fields.affected)
  }
  if (fields.affectedDevices) body.ticket_affected_devices = fields.affectedDevices.map(String)
  if (fields.affectedUser) body.ticket_affected_user = String(fields.affectedUser)
  /* Trimise si goale, ca in portal: o lista goala inseamna "nimeni", si trebuie
     sa poata sterge - de-aia se uita la `!== undefined`, nu la lungime. */
  if (fields.subscribers !== undefined) body.ticket_subscribers = fields.subscribers.map(String)
  if (fields.notifiers !== undefined) body.ticket_notifiers = fields.notifiers.map(String)
  if (fields.cause) body.cause = fields.cause
  if (fields.resolution) body.resolution = fields.resolution
  if (fields.updateComment) body.update_comment = fields.updateComment
  Object.assign(body, fields.extra ?? {})

  return postForm<NewTicketAnswer>(BASE, body, files)
}

/* -------------------------------------------------------------------------- */
/*  FORMULARELE FILEI "Change" - descrierea lor, pe un singur cap.             */
/*  Perechile vechi: `easeedesk/changeTicketStatus`, `updateTicketForm`,       */
/*  `planningTicketForm`, `escalateTicketForm`, `removeEscalation`,            */
/*  `resumePlanning` - fiecare intorcea o BUCATA DE MACHETA.                   */
/* -------------------------------------------------------------------------- */

export type TicketFormName = 'advance' | 'update' | 'planning' | 'escalate' | 'unescalate' | 'resume'

export interface TicketFormOption {
  /** Numar pentru listele din baza; TEXT pentru optiunile campurilor proprii, unde in baza sta chiar sirul. */
  value: number | string
  name: string
  /** Un rand mic dupa nume - felul echipamentului, clasa elementului de configurare. */
  subtext?: string
  /** Numele grupului din lista (un `<optgroup>`): furnizorii aplicatiilor tichetului, sus. */
  group?: string
}

/**
 * Un camp asa cum il descrie serverul.
 *
 * `key` e numele sub care pleaca inapoi - `ticket_priority`, `comments`, sau
 * `customfield_12` pentru campurile pe care si le-a adaugat clientul. `value` e
 * ce are tichetul acum: un sir, sau o lista cand campul tine mai multe.
 */
export interface TicketFormField {
  key: string
  custom: boolean
  id: number
  label: string
  type: string
  required: boolean
  options: TicketFormOption[]
  value: string | string[] | null
  /**
   * Numai pe `external_provider`: furnizorul aplicatiei celei mai critice de pe
   * tichet, care vine si ales in `value` (0 = tichetul n-are aplicatii cu furnizor).
   */
  suggested_provider_id?: number
  /** Numai pe `third_party_email`: sabloanele si ce se pune in ele. */
  email?: ThirdPartyEmailSpec
}

/**
 * Emailul catre furnizor din "Waiting for Third Party" (`ThirdPartyEscalation`
 * pe server): sabloanele (implicitul intai), variabilele, valorile comune ale
 * tichetului, unde pleaca emailul pe fiecare furnizor (`recipients`, gol = n-are
 * adresa; numai de aratat - serverul o socoteste iar la trimitere), si - pe
 * furnizorii care sunt ai unei aplicatii de pe tichet - aplicatia si valorile ei.
 */
export interface ThirdPartyEmailSpec {
  templates: ThirdPartyTemplate[]
  variables: Array<{ token: string; label: string }>
  values: Record<string, string>
  recipients?: Record<string, string>
  vendors: Record<string, { app: string; appId: number; email: string; values: Record<string, string> }>
  /**
   * Furnizor extern -> sablonul lui (7 octombrie 2026): ales singur cand e
   * ales furnizorul. Fara rand, implicitul general.
   */
  byProvider?: Record<string, number>
  /** Dreptul sabloanelor din Settings: arata "Save as template for <furnizor>". */
  canSaveTemplate?: boolean
}

export interface ThirdPartyTemplate {
  /** 0 = sablonul scris in cod, cand furnizorul de servicii n-are niciunul. */
  id: number
  name: string
  subject: string
  body: string
  isDefault: boolean
  builtin: boolean
  /** Furnizorii externi de care e legat sablonul. */
  providers?: number[]
}

export interface TicketActionForm {
  ticket: number
  form: TicketFormName
  name: string
  submit: string
  /** Pasul ales, pe formularul de avansare. */
  status: number
  stop_sla?: number
  /** Planning: termenul tichetului (UTC), ca ecranul sa avertizeze cand data aleasa il depaseste. */
  duedate?: string | null
  escalation_type?: number
  fields: TicketFormField[]
  /** De ce nu se poate deschide. `null` cand se poate. */
  blocked: { error: string; message: string } | null
}

export interface TicketFormAsk {
  /** Felul escaladarii: 1 intern, 2 client, 3 furnizor din afara. */
  type?: number
  /** Planificarea care opreste SLA-ul ("Suspend service"). */
  stopSla?: boolean
  /** Pasul ales din sertarul de status - formularul se reincarca pe el. */
  status?: number
  /** Echipa aleasa pe escaladarea interna - oamenii ei vin cu reincarcarea. */
  group?: number
  /** Categoria aleasa (avansare, actualizare) - subcategoriile ei vin cu reincarcarea. */
  category?: number
  /** Subcategoria aleasa - operatiile ei vin cu reincarcarea. */
  subcategory?: number
}

/**
 * Descrierea unui formular.
 *
 * Se REINCARCA la schimbarea pasului sau a echipei, exact ca in portal, unde
 * `reloadForm()` cerea din nou toata bucata de macheta: fiecare pas are alt
 * formular, si oamenii atarna de echipa aleasa. De-aia cei doi parametri sunt
 * in cheia intrebarii.
 */
export function ticketActionFormQuery(id: number, form: TicketFormName, ask: TicketFormAsk = {}) {
  const params = new URLSearchParams()
  if (ask.type) params.set('type', String(ask.type))
  if (ask.stopSla) params.set('stop_sla', '1')
  if (ask.status) params.set('status', String(ask.status))
  if (ask.group) params.set('group', String(ask.group))
  if (ask.category) params.set('category', String(ask.category))
  if (ask.subcategory) params.set('subcategory', String(ask.subcategory))

  const tail = params.toString()

  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'action-form', form, tail],
    queryFn: () => apiGet<TicketActionForm>(`${BASE}/${id}/action-form/${form}${tail === '' ? '' : `?${tail}`}`),
    /* Formularul de dinainte ramane pe ecran cat vine cel nou. Fara asta, o
       schimbare de pas ar fi lasat in loc o rotita, si tot ce completase omul
       ar fi disparut o clipa - lucru care se citeste ca pierdere, chiar daca
       valorile se intorc. */
    placeholderData: keepPreviousData,
  })
}

/**
 * Actualizarea unui tichet - `easeedesk/updateTicket`, butonul "Update".
 *
 * Campurile sunt cele ale formularului, cu numele din portal, fiindca tot ele
 * ajung in `it_easeedesk_tickets`.
 */
export function updateTicket(
  id: number,
  fields: Record<string, string | string[]>,
  files: File[] = [],
): Promise<{ status: string; ticket: number }> {
  return postForm(`${BASE}/${id}/update`, fields, files)
}

/* -------------------------------------------------------------------------- */
/*  AVANSAREA TICHETULUI - butonul "Advance" din panoul "Change".              */
/*  Perechea veche: `easeedesk/changeStatusTicket`.                            */
/* -------------------------------------------------------------------------- */

/** Pasii legali de acum incolo - lista pe care o desena formularul vechi. */
export interface TicketSteps {
  ticket: number
  status: number
  next: number[]
}

export function ticketStepsQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'steps'],
    queryFn: () => apiGet<TicketSteps>(`${BASE}/${id}/steps`),
  })
}

/**
 * Campurile formularului de avansare.
 *
 * Numele sunt cele din portal, fiindca tot ele ajung in `it_easeedesk_tickets`:
 * `ticket_status` e pasul ales, `cause` / `resolution` / `update_comment` sunt
 * cele trei feluri de comentariu, iar `customfield_<id>` sunt campurile pe care
 * si le-a adaugat clientul.
 */
export interface TicketStatusFields {
  ticketStatus: number
  cause?: string
  resolution?: string
  updateComment?: string
  /**
   * Abonatii si instiintatii, cand formularul pasului ii deseneaza.
   *
   * Trimise si goale: o lista goala inseamna "nimeni", si trebuie sa poata
   * sterge - de-aia `undefined` si `[]` NU inseamna acelasi lucru aici.
   */
  subscribers?: number[]
  notifiers?: number[]
  /** `customfield_12`, `ticket_priority`, `ticket_affected`, ... - ce cere formularul pasului. */
  extra?: Record<string, string | string[]>
}

export function changeTicketStatus(
  id: number,
  fields: TicketStatusFields,
  files: File[] = [],
): Promise<{ status: string; ticket: number; ticketStatus: number }> {
  const body: Record<string, string | string[]> = { ticket_status: String(fields.ticketStatus) }
  if (fields.cause) body.cause = fields.cause
  if (fields.resolution) body.resolution = fields.resolution
  if (fields.updateComment) body.update_comment = fields.updateComment
  if (fields.subscribers !== undefined) body.ticket_subscribers = fields.subscribers.map(String)
  if (fields.notifiers !== undefined) body.ticket_notifiers = fields.notifiers.map(String)
  Object.assign(body, fields.extra ?? {})

  return postForm(`${BASE}/${id}/status`, body, files)
}

/**
 * Fisierele unui tichet - `easeedesk/addAttachments`.
 *
 * Nu se scriu in baza portalului: pleaca la serverul de descarcari, si el le
 * tine. Ce se intoarce e descrierea fiecarui fisier primit.
 */
export interface TicketAttachment {
  filename: string
  mimetype: string
  filesize: number
  is_image?: number
  thumbnail?: string
}

export function uploadTicketAttachments(
  id: number,
  files: File[],
): Promise<{ status: string; files: TicketAttachment[] }> {
  const body = new FormData()
  for (const file of files) body.append('files[]', file)

  return apiPostForm(`${BASE}/${id}/attachments`, body)
}

/**
 * Fisierele unei solutii, copiate pe tichet - `easeedesk/attachSolutionFiles`
 * (fila IT Solutions, "Attach to ticket"). `ids` sunt `it_solutions_attachments.ID`.
 */
export function attachSolutionFiles(
  ticket: number,
  ids: number[],
): Promise<{ status: string; copied?: number; failed?: string[]; message: string }> {
  return apiPost(`${BASE}/${ticket}/solution-attachments`, { attachments: ids })
}

/**
 * Un articol legat de tichet FARA fisiere - acelasi capat, cu `solutions[]`
 * in loc de `attachments[]`. `solution` e `it_solutions_company.ID` (`SolutionEntry.ID`).
 * Asa se leaga si un articol care n-are niciun atasament.
 */
export function linkSolutionToTicket(
  ticket: number,
  solution: number,
): Promise<{ status: string; linked?: number; message: string }> {
  return apiPost(`${BASE}/${ticket}/solution-attachments`, { solutions: [solution] })
}

/**
 * Redeschiderea unui tichet rezolvat - `easeedesk/resumeResolvedTicket`.
 *
 * Butonul ii ia locul lui "Advance" pe un tichet rezolvat, deci si dreptul e tot
 * al lui.
 */
export function reopenResolvedTicket(
  id: number,
  comments?: string,
): Promise<{ status: string; ticket: number; ticketStatus: number }> {
  return apiPost(`${BASE}/${id}/reopen`, comments ? { comments } : {})
}

/* -------------------------------------------------------------------------- */
/*  PLANIFICAREA - butonul "Plan" si intrarea "Suspend service" din lista.     */
/*  Perechile vechi: `easeedesk/planningTicket`, `resumePlannedTicket`,        */
/*  `multiselectSuspendService`.                                              */
/* -------------------------------------------------------------------------- */

/**
 * Ora la care se reia tichetul, in UTC, cum o cere capatul.
 *
 * Portalul primea ora locala si o muta el pe server, dupa fusul din sesiune.
 * Aici o mutam in browser, care stie exact fusul omului: `toISOString()` da
 * mereu UTC, si nu mai ramane nimic de ghicit.
 */
export function toUtcStamp(local: Date): string {
  return local.toISOString().slice(0, 19).replace('T', ' ')
}

export function planTicket(
  id: number,
  fields: { plannedAt: Date; comments?: string; notifier?: number; stopSla?: boolean },
): Promise<{ status: string; ticket: number; planning: number }> {
  const body: Record<string, string> = { planned_datetime: toUtcStamp(fields.plannedAt) }
  if (fields.comments) body.comments = fields.comments
  if (fields.notifier) body.notifier = String(fields.notifier)
  if (fields.stopSla) body.stop_sla = '1'

  return apiPost(`${BASE}/${id}/plan`, body)
}

export function resumePlannedTicket(id: number, comments: string): Promise<{ status: string; ticket: number }> {
  return apiPost(`${BASE}/${id}/resume`, { comments })
}

export interface SuspendReply {
  status: string
  planned: number
  skipped: number
}

export function suspendTickets(ids: number[], plannedAt: Date, comments?: string): Promise<SuspendReply> {
  const body: Record<string, string | number[]> = {
    tickets: ids,
    planned_datetime: toUtcStamp(plannedAt),
  }
  if (comments) body.comments = comments

  return apiPost<SuspendReply>(`${BASE}/suspend`, body)
}

/* -------------------------------------------------------------------------- */
/*  ESCALADAREA - cele trei butoane "Escalate", "Waiting for Customer" si      */
/*  "Waiting for Third Party". Perechea veche: `easeedesk/escalateTicket`.     */
/* -------------------------------------------------------------------------- */

/** `it_easeedesk_ticket_escalations.escalation_type`, ca in portal. */
export const ESCALATION_TYPES = { internal: 1, customer: 2, thirdParty: 3 } as const
export type EscalationType = (typeof ESCALATION_TYPES)[keyof typeof ESCALATION_TYPES]

export interface EscalationFields {
  comments?: string
  /** Felul 1: la grup, sau la un om anume. */
  group?: number
  user?: number
  /** Felul 1: reclasifica tichetul; alt tip inseamna alt flux, deci alt status. */
  ticketType?: number
  /** Felurile 2 si 3: pana cand se asteapta. Se trimite in UTC. */
  expiresAt?: Date
  /** Felul 2. */
  customer?: number
  /** Felul 3. */
  externalProvider?: number
  escalationReason?: string
  /** Felul 3: bifa "Show in agent". */
  notifyAgent?: boolean
  /** Felul 3: emailul catre furnizor, GATA UMPLUT. Destinatarul il pune serverul. */
  email?: { subject: string; body: string }
}

export function escalateTicket(
  id: number,
  type: EscalationType,
  fields: EscalationFields,
): Promise<{ status: string; ticket: number; escalation: number; message?: string; emailSent?: boolean; vendorCase?: number }> {
  const body: Record<string, string> = {}
  if (fields.comments) body.comments = fields.comments
  if (fields.group) body.group = String(fields.group)
  if (fields.user) body.user = String(fields.user)
  if (fields.ticketType) body.ticket_type = String(fields.ticketType)
  if (fields.expiresAt) body.expiry_datetime = toUtcStamp(fields.expiresAt)
  if (fields.customer) body.customer = String(fields.customer)
  if (fields.externalProvider) body.external_provider = String(fields.externalProvider)
  if (fields.escalationReason) body.escalation_reason = fields.escalationReason
  if (fields.notifyAgent) body.notify_agent = '1'
  if (fields.email) {
    body.email_subject = fields.email.subject
    body.email_body = fields.email.body
  }

  return apiPost(`${BASE}/${id}/escalate/${type}`, body)
}

/* Furnizorul extern din fereastra de escaladare (`tickets/details/modals.php`):
   `easeedesk/getExternalProviderSettings/{id}`, `easeedesk/createExternalProvider`,
   `easeedesk/saveExternalProviderSettings`. */

export interface ExternalProvider {
  ID: number
  name: string
  email: string
  phone: string
}

export interface ExternalProviderFields extends Record<string, string> {
  provider_name: string
  provider_email: string
  provider_phone: string
}

export function externalProviderQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'external-provider', id],
    queryFn: () => apiGet<ExternalProvider>(`/easeedesk/external-providers/${id}`),
  })
}

export function createExternalProvider(fields: ExternalProviderFields): Promise<{ status: string; id: number }> {
  return apiPost('/easeedesk/external-providers', fields)
}

export function saveExternalProvider(id: number, fields: ExternalProviderFields): Promise<{ status: string; id: number }> {
  return apiPost(`/easeedesk/external-providers/${id}`, fields)
}

export function removeTicketEscalation(id: number, comments?: string): Promise<{ status: string; ticket: number }> {
  return apiPost(`${BASE}/${id}/unescalate`, comments ? { comments } : {})
}

/**
 * Drepturile meniului "Actions" de deasupra listei.
 *
 * In portal se citeau in macheta si coborau in javascript ca `hasAccessMerge`
 * si celelalte trei (`easeedesk/tickets/index.php`, randurile 201-204). Aici se
 * cer o data, de la `/me?can=`, cu ACELEASI patru slug-uri.
 */
export const BULK_RIGHTS = {
  merge: 'easeedesk.tickets_actions_merge.read',
  suspend: 'easeedesk.tickets_actions_suspend_service.read',
  assign: 'easeedesk.tickets_actions_assign_operator.read',
  trash: 'easeedesk.tickets_actions_move_to_trash.read',
} as const

export const bulkRightsQuery = queryOptions({
  queryKey: ['easeedesk', 'bulk-rights'],
  queryFn: () => apiGet<{ can: Record<string, boolean> }>(`/me?can=${Object.values(BULK_RIGHTS).join(',')}`),
  staleTime: 5 * 60 * 1000,
})
